import { useEffect, useState } from "react";
import { Spinner, useToast } from "@chakra-ui/react";
import { useConfirmation } from "../../contexts/ConfirmationContext";
import {
  CreateUpdateQuotationInput,
  Quotation,
  useCreateQuotationMutation,
  useGetQuotationLazyQuery,
  useUpdateQuotationMutation,
} from "../../generated/graphql";
import { useErrorMessage } from "../../hooks/errorMessage";
import {
  MCFormQuotationDetail,
  MCFormQuotationItems,
} from "../../components/MCFormQuotation";
import {
  FormMutationType,
  InheritFullWidthModalProps,
  SaveType,
} from "../../components/FullWidthModal/interface";
import FullWidthModal from "../../components/FullWidthModal";
import { IMCFormQuotationItems } from "../../components/MCFormQuotation";
import moment from "moment";
import { IMCFormQuotationDetail } from "../../components/MCFormQuotation/interface";

const navigations = ["Detail Quotation", "Detail Item"];

const QuotationForm: React.FC<InheritFullWidthModalProps<Quotation>> = ({
  id,
  onClose,
  isOpen,
  ...props
}) => {
  const [getQuotation, { data, loading: isLoadingData }] =
    useGetQuotationLazyQuery();

  useEffect(() => {
    if (data) {
      const {
        itinerary,
        itineraryDate,
        to,
        additionalCost,
        bookingCode,
        quotationItems,
        signatureId,
        signature,
        signatureBackgroundId,
        signatureBackground,
      } = data?.getQuotation as Quotation;

      setDetailValue({
        itinerary,
        itineraryDate,
        to,
        bookingCode,
        signature: signatureId ? signature : null,
        signatureBackground: signatureBackgroundId ? signatureBackground : null,
      });

      setItemsValue({
        items: quotationItems.map(
          ({ __typename, quotationItemFlights, ...elm }) => ({
            ...elm,
            flights: quotationItemFlights.map(({ __typename, ...elm }) => ({
              ...elm,
            })),
          })
        ),
        additionalCost: additionalCost || 0,
      });
    }
  }, [data]);

  const { confirmation, setIsLoading, close } = useConfirmation();

  const toast = useToast();

  const { showErrorMessage } = useErrorMessage();

  const [detailValue, setDetailValue] = useState<IMCFormQuotationDetail>();
  const [itemsValue, setItemsValue] = useState<IMCFormQuotationItems>();
  const [createQuotationMutation] = useCreateQuotationMutation();
  const [updateQuotationMutation] = useUpdateQuotationMutation();

  useEffect(() => {
    if (id && isOpen) {
      getQuotation({ variables: { id } });
    }
  }, [id, isOpen, getQuotation]);

  const [screenIndex, setScreenIndex] = useState(0);

  const reset = () => {
    setScreenIndex(0);
    setDetailValue(undefined);
    setItemsValue(undefined);
  };

  const saveQuotation = async ({
    signature,
    signatureBackground,
    ...data
  }: IMCFormQuotationDetail & IMCFormQuotationItems) => {
    try {
      setIsLoading(true);

      const input: CreateUpdateQuotationInput = {
        ...data,
        additionalCost: data.additionalCost ? data.additionalCost : undefined,
        itineraryDate: moment(data.itineraryDate).format("Y-MM-DD 00:00:00"),
        signatureId: signature?.id || null,
        signatureBackgroundId: signatureBackground?.id || null,
        items: data.items.map(({ ...elm }) => ({
          ...elm,
          numberOfPax: parseInt(elm.numberOfPax.toString()),
          pricePerPax: parseFloat(elm.pricePerPax.toString()),
        })),
      };

      if (id) {
        const res = await updateQuotationMutation({
          variables: {
            id,
            input,
          },
        });
        toast({
          description: "Quotation successfully udpated!",
          status: "success",
        });
        onClose({
          type: FormMutationType.UPDATE,
          data: res.data?.updateQuotation as Quotation,
        });
      } else {
        await createQuotationMutation({
          variables: {
            input,
          },
        });
        toast({
          description: "Quotation successfully added!",
          status: "success",
        });
        onClose({ type: FormMutationType.CREATE });
      }

      reset();
      close();
    } catch (e) {
      showErrorMessage(e);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FullWidthModal
      title={id ? "Edit Quotation" : "New Quotation"}
      onClose={() => {
        reset();
        onClose();
      }}
      isOpen={isOpen}
      {...props}
    >
      {isLoadingData ? (
        <Spinner />
      ) : (
        <>
          {screenIndex === 1 ? (
            <MCFormQuotationItems
              value={itemsValue}
              navigations={navigations.map((elm, index) => ({
                label: elm,
                isActive: index === 1,
              }))}
              onBack={(data) => {
                setItemsValue(data);
                setScreenIndex((index) => index - 1);
              }}
              onSubmit={(value, type) => {
                confirmation({
                  description: "Are you sure want to save?",
                  callback: () => {
                    saveQuotation({
                      ...(detailValue as CreateUpdateQuotationInput),
                      ...value,
                    });
                  },
                });
              }}
            />
          ) : (
            <MCFormQuotationDetail
              navigations={navigations.map((elm, index) => ({
                label: elm,
                isActive: index === 0,
              }))}
              value={detailValue}
              onBack={() => {
                onClose();
              }}
              onSubmit={async (value, type) => {
                if (type === SaveType.DRAFT) {
                  console.log("Save to draft");
                } else {
                  setDetailValue(value);
                  setScreenIndex((i) => i + 1);
                }
              }}
            />
          )}
        </>
      )}
    </FullWidthModal>
  );
};

export default QuotationForm;
